import React from 'react';
import {StyleSheet, View} from 'react-native';

import AmexIcon from '../../../../images/brand/svg/icons/Amex.svg';
import MastercardIcon from '../../../../images/brand/svg/icons/Mastercard.svg';
import VisaIcon from '../../../../images/brand/svg/icons/Visa.svg';
import theme from '../../../../style/theme';
import Text from '../../../themed/Text';
import MonospaceText from '../../../ui-controls/MonospaceText';

type CreditCardVisualProps = {
  cardType?: string;
  lastFourDigits?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isEmpty?: boolean;
};

const CardChip = () => (
  <View style={styles.chip}>
    <View style={styles.chipLines} />
  </View>
);

const CardLogo = ({cardType}: {cardType?: string}) => {
  if (cardType?.toLowerCase() === 'visa') {
    return <VisaIcon width={60} height={40} />;
  }
  if (cardType?.toLowerCase() === 'mastercard') {
    return <MastercardIcon width={60} height={40} />;
  }
  if (cardType?.toLowerCase() === 'amex' || cardType?.toLowerCase() === 'american express') {
    return <AmexIcon width={60} height={40} />;
  }
  return null;
};

const formatCardNumber = (lastFour?: string) => `**** **** **** ${lastFour || '****'}`;

const formatExpiry = (month?: number, year?: number) => {
  if (!month || !year) return '--/--';
  const mm = month.toString().padStart(2, '0');
  const yy = year.toString().slice(-2);
  return `${mm}/${yy}`;
};

export default function CreditCardVisual({
  cardType,
  lastFourDigits,
  expiryMonth,
  expiryYear,
  isEmpty = false,
}: CreditCardVisualProps) {
  if (isEmpty) {
    return (
      <View style={[styles.card, styles.cardEmpty]}>
        <View style={styles.emptyIconRow}>
          <VisaIcon width={40} height={26} />
          <MastercardIcon width={40} height={26} />
          <AmexIcon width={40} height={26} />
        </View>
        <Text style={styles.emptyText}>Add a payment card</Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.topRow}>
        <CardChip />
        <CardLogo cardType={cardType} />
      </View>
      <View style={styles.numberRow}>
        <MonospaceText
          style={styles.cardNumber}
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.7}>
          {formatCardNumber(lastFourDigits)}
        </MonospaceText>
      </View>
      <View style={styles.expiryRow}>
        <Text style={styles.expiryLabel}>VALID THRU</Text>
        <Text style={styles.expiryValue}>{formatExpiry(expiryMonth, expiryYear)}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    aspectRatio: 1.586,
    maxWidth: 340,
    minWidth: 280,
    width: '100%',
    backgroundColor: theme.colour.creditCard?.background ?? theme.colour.general.secondary,
    borderRadius: 12,
    padding: 20,
    justifyContent: 'space-between',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  cardEmpty: {
    backgroundColor: '#E0E0E0',
    borderWidth: 2,
    borderColor: '#BDBDBD',
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  chip: {
    width: 40,
    height: 30,
    backgroundColor: theme.colour.creditCard?.chip ?? '#D4AF37',
    borderRadius: 4,
    overflow: 'hidden',
  },
  chipLines: {
    position: 'absolute',
    top: 8,
    left: 4,
    right: 4,
    height: 14,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(0,0,0,0.2)',
  },
  numberRow: {
    marginTop: 16,
  },
  cardNumber: {
    fontSize: 18,
    letterSpacing: 2,
    color: theme.colour.creditCard?.text ?? '#FFFFFF',
  },
  expiryRow: {
    marginTop: 8,
  },
  expiryLabel: {
    fontSize: 10,
    color: theme.colour.creditCard?.textMuted ?? 'rgba(255,255,255,0.7)',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  expiryValue: {
    fontSize: 16,
    color: theme.colour.creditCard?.text ?? '#FFFFFF',
    fontWeight: '500',
    marginTop: 2,
  },
  emptyIconRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  emptyText: {
    color: '#757575',
    fontSize: 16,
    fontWeight: '500',
  },
});
